import { QueryKey } from '@crm/api'
import { Options, TDataShape } from '@hey-api/client-axios'
import { useDebounce } from '@shared/hooks'
import { OptionTransformType, transformOptions } from '@shared/utils'
import {
  InfiniteData,
  useInfiniteQuery,
  UseInfiniteQueryOptions
} from '@tanstack/react-query'
import { Empty, Flex, Select, Spin } from 'antd'
import { AnyObject } from 'antd/es/_util/type'
import { DefaultOptionType, SelectProps } from 'antd/es/select'
import { AxiosError } from 'axios'
import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'

type Props<
  TData extends TDataShape,
  TPaginatedData extends AnyObject,
  TRetrieveData
> = {
  getQueryFunction: (
    options: Options<TData>
  ) => UseInfiniteQueryOptions<
    TPaginatedData,
    AxiosError<Error, unknown>,
    InfiniteData<TPaginatedData>,
    TPaginatedData,
    QueryKey<Options<TData>>,
    | number
    | Pick<QueryKey<Options<TData>>[0], 'body' | 'headers' | 'path' | 'query'>
  >
  baseOptions?: Options<TData>
  getOptions?: (params: OptionTransformType<TRetrieveData>) => DefaultOptionType
  queryDisabled?: boolean
} & SelectProps

export const InfiniteSelect = <
  TData extends TDataShape,
  TPaginatedData extends AnyObject,
  TRetrieveData
>({
  getQueryFunction,
  baseOptions,
  getOptions = transformOptions,
  queryDisabled = false,
  ...props
}: Props<TData, TPaginatedData, TRetrieveData>) => {
  const { t } = useTranslation()

  const [search, setSearch] = useState<string>()
  const debouncedSearch = useDebounce(search)

  const mergedOptions = useMemo<Options<TData>>(() => {
    const base = (baseOptions ?? {}) as Options<TData>
    const baseQuery = (base.query ?? {}) as Record<string, unknown>

    const pref = baseQuery.preference

    const getIsValidPreference = (value: unknown) =>
      value !== undefined &&
      value !== null &&
      !(Array.isArray(value) && value.length === 0)

    const shouldUsePref = props.labelInValue || getIsValidPreference(pref)

    const preference = shouldUsePref ? pref : props.value

    return {
      ...base,
      query: {
        preference,
        ...baseQuery,
        search: debouncedSearch
      }
    }
  }, [baseOptions, debouncedSearch, props.labelInValue, props.value])

  const {
    data,
    fetchNextPage,
    isFetching,
    isLoading: isDataLoading,
    hasNextPage
  } = useInfiniteQuery({
    ...getQueryFunction(mergedOptions),
    getNextPageParam: (lastPage) => lastPage?.next,
    initialPageParam: 1,
    enabled: !queryDisabled
  })

  const options = useMemo(() => {
    return data?.pages?.flatMap((page) =>
      getOptions({ options: page?.results })
    )
  }, [data, getOptions])

  const isLoading = isFetching || isDataLoading

  return (
    <Select
      options={options}
      loading={isLoading}
      searchValue={search}
      allowClear
      showSearch
      filterOption={false}
      popupMatchSelectWidth={false}
      onSearch={(value) => setSearch(value)}
      placeholder={t('main.select')}
      onPopupScroll={(e) => {
        const target = e?.target as HTMLElement

        if (
          hasNextPage &&
          !isFetching &&
          target.scrollHeight - target.scrollTop <= target.clientHeight + 1
        ) {
          fetchNextPage()
        }
      }}
      {...props}
      notFoundContent={
        isLoading ? (
          <Flex justify="center">
            <Spin />
          </Flex>
        ) : (
          <Empty />
        )
      }
      value={isLoading ? null : props.value}
    />
  )
}
